import React, { useEffect, useRef } from 'react';

// Nápis složený z teček (inspirace: patička unitedcarriers.com).
// Tečky se při zobrazení slétnou na svá místa, kurzor je rozfoukává
// a rozfoukané tečky se na chvíli rozsvítí akcentovou barvou.
const FONT_FAMILY = '"Barlow Condensed", "Space Grotesk", sans-serif';
const BASE = [243, 241, 236];
const ACCENT = [255, 164, 27];
const BASE_ALPHA = 0.32;
const HEAT_LEVELS = 6;

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const PALETTE = Array.from({ length: HEAT_LEVELS }, (_, i) => {
  const t = i / (HEAT_LEVELS - 1);
  const [r, g, b] = mix(BASE, ACCENT, t);
  return `rgba(${r}, ${g}, ${b}, ${BASE_ALPHA + (1 - BASE_ALPHA) * t})`;
});

const ParticleWordmark = ({ text = 'KOVOJOCH', className = '' }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let particles = [];
    let dpr = 1;
    let size = 1;
    let radius = 1;
    let raf = 0;
    let visible = false;
    let started = false;
    let destroyed = false;
    const mouse = { x: -1e4, y: -1e4, active: false, timer: 0 };

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssW = wrap.clientWidth;
      if (!cssW) return;

      // Velikost písma tak, aby nápis vyplnil celou šířku
      const probe = document.createElement('canvas').getContext('2d');
      probe.font = `700 100px ${FONT_FAMILY}`;
      const m = probe.measureText(text);
      const scale = cssW / (m.actualBoundingBoxLeft + m.actualBoundingBoxRight);
      const ascent = m.actualBoundingBoxAscent * scale;
      const textH = ascent + m.actualBoundingBoxDescent * scale;
      const pad = Math.round(textH * 0.3);
      const cssH = Math.ceil(textH + pad * 2);

      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.height = `${cssH}px`;

      // Nápis se nakreslí mimo obrazovku a z jeho pixelů se vzorkují pozice teček
      const off = document.createElement('canvas');
      off.width = canvas.width;
      off.height = canvas.height;
      const octx = off.getContext('2d');
      octx.font = `700 ${100 * scale * dpr}px ${FONT_FAMILY}`;
      octx.fillStyle = '#fff';
      octx.fillText(text, m.actualBoundingBoxLeft * scale * dpr, (pad + ascent) * dpr);
      const { data } = octx.getImageData(0, 0, off.width, off.height);

      const gap = Math.round(Math.min(5, Math.max(3, cssW / 340)) * dpr);
      size = gap * 0.62;
      radius = 80 * dpr;

      const prev = particles;
      particles = [];
      for (let y = 0; y < off.height; y += gap) {
        for (let x = 0; x < off.width; x += gap) {
          if (data[(y * off.width + x) * 4 + 3] > 128) {
            particles.push({
              ox: x,
              oy: y,
              x: started ? x : Math.random() * off.width,
              y: started ? y : Math.random() * off.height * 1.6 - off.height * 0.3,
              vx: 0,
              vy: 0,
              ease: 0.04 + Math.random() * 0.04,
              friction: 0.8 + Math.random() * 0.15,
              heat: 0,
            });
          }
        }
      }
      if (reduce) {
        particles.forEach((p) => {
          p.x = p.ox;
          p.y = p.oy;
        });
      }
      if (prev.length && started) draw();
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Tečky se kreslí po skupinách podle barvy – jeden fill na skupinu je výrazně rychlejší
      const paths = PALETTE.map(() => new Path2D());
      for (const p of particles) {
        const level = Math.min(HEAT_LEVELS - 1, Math.round(p.heat * (HEAT_LEVELS - 1)));
        paths[level].rect(p.x - size / 2, p.y - size / 2, size, size);
      }
      paths.forEach((path, i) => {
        ctx.fillStyle = PALETTE[i];
        ctx.fill(path);
      });
    };

    const tick = () => {
      raf = 0;
      let moving = false;
      const r2 = radius * radius;
      for (const p of particles) {
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < r2 && d2 > 0.01) {
            const d = Math.sqrt(d2);
            const force = (-(radius - d) / radius) * 7 * dpr * (0.5 + Math.random());
            p.vx += (dx / d) * force + (Math.random() - 0.5);
            p.vy += (dy / d) * force + (Math.random() - 0.5);
            p.heat = 1;
          }
        }
        p.vx *= p.friction;
        p.vy *= p.friction;
        p.x += p.vx + (p.ox - p.x) * p.ease;
        p.y += p.vy + (p.oy - p.y) * p.ease;
        p.heat *= 0.965;
        if (!moving && (Math.abs(p.ox - p.x) > 0.4 || Math.abs(p.oy - p.y) > 0.4 || p.heat > 0.02)) {
          moving = true;
        }
      }
      draw();
      // Když se nic nehýbe, smyčka se zastaví a nežere výkon
      if (visible && (moving || mouse.active)) raf = requestAnimationFrame(tick);
    };

    const kick = () => {
      if (!raf && visible && started && !destroyed) raf = requestAnimationFrame(tick);
    };

    const onPointerMove = (e) => {
      if (e.pointerType !== 'mouse' || !visible || reduce) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < -80 || y < -80 || x > rect.width + 80 || y > rect.height + 80) return;
      mouse.x = x * dpr;
      mouse.y = y * dpr;
      mouse.active = true;
      clearTimeout(mouse.timer);
      mouse.timer = setTimeout(() => (mouse.active = false), 100);
      kick();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !started && entry.intersectionRatio >= 0.3) {
          started = true;
          if (reduce) draw();
        }
        if (!visible && raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
        kick();
      },
      { threshold: [0, 0.3] }
    );

    const ro = new ResizeObserver(() => {
      build();
      kick();
    });

    document.fonts.load(`700 100px ${FONT_FAMILY}`).finally(() => {
      if (destroyed) return;
      build();
      ro.observe(wrap);
      io.observe(canvas);
      window.addEventListener('pointermove', onPointerMove, { passive: true });
    });

    return () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      clearTimeout(mouse.timer);
      io.disconnect();
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [text]);

  return (
    <div ref={wrapRef} className={`particle-wordmark ${className}`} aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ParticleWordmark;
